import React, {useRef, useState} from 'react'

import Guid from '../../../../../utils/guid.js'

import {Alert, Button, Card, H3, ListItem, P, Panel, Popup, Row} from '@components/UI/index.js'
import ShiftPriceTableModal from '@components/shifts/ShiftPriceTableModal.js'
import naturalSortBy from 'src/utils/naturalSortBy.js'

import {Reseller} from 'types/types.js'
import {ShiftPriceTable} from 'server/functions/resellers/resellers.types.js'
import {formSpacing} from '../../../../components/UI/forms/index.js'
import type {PopupRef} from '../../../../components/UI/overlays/Popup.js'

interface ShiftPricesProps {
    reseller: Reseller
    onChange: (reseller: Reseller, path: string) => void
}

const INVALID_FILE = 'Dit bestand heeft geen geldige opmaak. Er kan alleen een JSON bestand geïmporteerd worden dat ook vanuit Veloyd geëxporteerd is en dat niet is bewerkt.'

const ShiftPrices = ({reseller, onChange}: ShiftPricesProps) => {
    const [error, setError] = useState('')
    const modal = useRef<ShiftPriceTableModal>(null)
    const popup = useRef<PopupRef>(null)
    const fileInput = useRef<HTMLInputElement>(null)

    const prices = reseller.settings.shifts.prices || []

    const save = (newPrices: ShiftPriceTable[]) => {
        const updated = {...reseller}
        updated.settings.shifts.prices = newPrices
        onChange(updated, 'shifts.prices')
    }

    const onSubmit = (priceTable: ShiftPriceTable) => {
        const index = prices.findIndex((pt) => pt.id === priceTable.id)

        save(index === -1 ? [...prices, priceTable] : prices.map((pt) => pt.id === priceTable.id ? priceTable : pt))
    }

    const onCopy = (event: React.MouseEvent, priceTable: ShiftPriceTable) => {
        event.stopPropagation()

        save([...prices, {...structuredClone(priceTable), id: Guid.generate(), name: `${priceTable.name} (Kopie)`}])
    }

    const onRemove = (event: React.MouseEvent, id: string) => {
        event.stopPropagation()

        popup.current.open('Tarieftabel verwijderen', 'Weet je zeker dat je deze tarieftabel wilt verwijderen? Diensten met deze tarieftabel krijgen bij het herberekenen geen prijs meer.', () => {
            save(prices.filter((priceTable) => priceTable.id !== id))
            popup.current.close()
        })
    }

    const onExport = () => {
        const exported = structuredClone(prices).map((priceTable) => ({...priceTable, id: Guid.generate()}))

        const blob = new Blob([JSON.stringify(exported)], {type: 'application/json'})

        const linkEl = document.createElement('a')
        linkEl.href = URL.createObjectURL(blob)
        linkEl.setAttribute('download', `${reseller.settings?.accountName || reseller.name} Tarieven diensten`)
        linkEl.click()
    }

    const onImport = () => {
        setError('')
        const file = fileInput.current?.files?.[0]

        if (!file) return

        const reader = new FileReader()
        reader.readAsText(file)
        reader.onload = (event) => {
            try {
                const imported = JSON.parse(event.target.result as string) as ShiftPriceTable[]

                if (!Array.isArray(imported) || imported.length === 0 || imported.some((priceTable) => !priceTable.id || !priceTable.name || !priceTable.priceBasedOn)) {
                    throw new Error(INVALID_FILE)
                }

                save([...prices, ...imported])
            } catch {
                setError(INVALID_FILE)
            }
        }
    }

    return (
        <Card shadow style={{width: 600, padding: 24, height: 'fit-content'}}>
            <H3 style={{marginBottom: 36}}>Tarieven</H3>

            <P style={{marginBottom: 12}}>Tarieftabellen voor diensten. Een dienst krijgt alleen een prijs als er een tarieftabel bij de dienst is gekozen.</P>

            {error && <Alert style={{marginBottom: 6}} variant='error'>{error}</Alert>}

            <Row style={{columnGap: formSpacing.columnGap, marginBottom: 24}}>
                <Button
                    variant='outline'
                    icon='mdi mdi-file-export'
                    label='Tarieftabellen exporteren'
                    onClick={onExport}
                />

                <Button
                    variant='outline'
                    icon='mdi mdi-file-import'
                    label='Tarieftabellen importeren'
                    onClick={() => {
                        fileInput.current.value = null
                        fileInput.current.click()
                    }}
                />
            </Row>

            {prices.length > 0 &&
                <Panel style={{maxHeight: 500, overflowY: 'auto', marginBottom: 24}}>
                    {naturalSortBy(prices, 'name').map((priceTable: ShiftPriceTable) => (
                        <ListItem
                            key={`shiftPriceTable${priceTable.id}`}
                            onClick={() => modal.current.open(priceTable)}
                            iconButtons={[
                                {icon: 'mdi mdi-content-copy', onClick: (event) => onCopy(event, priceTable)},
                                {icon: 'mdi mdi-delete', onClick: (event) => onRemove(event, priceTable.id)}
                            ]}
                        >
                            {priceTable.name}
                        </ListItem>
                    ))}
                </Panel>
            }

            <Button
                variant='outline'
                icon='mdi mdi-plus'
                label='Tarieftabel toevoegen'
                onClick={() => modal.current.open()}
            />

            <ShiftPriceTableModal
                ref={modal}
                reseller={reseller}
                onSubmit={onSubmit}
            />

            <input
                type='file'
                ref={fileInput}
                accept='.json'
                style={{visibility: 'hidden', height: 0}}
                onChange={onImport}
            />

            <Popup ref={popup} />
        </Card>
    )
}

export default ShiftPrices
